import { Injectable } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { environment } from 'environments/environment';
import { catchError } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { AnalyticsService } from 'services';

export type ApiInterceptorHeaderKeys = 'x-api-interceptor-errors';

/**
 * Used only to prepend environment.origin to requestUrl
 * And display network error messages
 *
 * To Disable Errors set HttpHeader 'x-api-interceptor-errors': 'false'
 */
@Injectable({
	providedIn: 'root',
})
export class ApiInterceptor implements HttpInterceptor {
	constructor(private router: Router, private toastrService: ToastrService, private readonly analyticsService: AnalyticsService) {}

	public intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		const config = {
			showErrors: true,
			errorsHeader: <ApiInterceptorHeaderKeys>'x-api-interceptor-errors',
		};

		// Appending environment.origin only if url is not starts with protocol
		// and not lead to assets directory
		if (!req.url.match(new RegExp('^https?://')) && !req.url.match(new RegExp(`^${environment.assets}`))) {
			req = req.clone({
				url: environment.origin + req.url,
			});
		}

		// Adding app to header only if url is not starts with protocol
		// and not lead to assets directory
		if (req.url.match(new RegExp('^' + environment.origin))) {
			req = req.clone();
		}

		// For API requests changind Content-Type from text/plain to application/json
		// for string or number body variables and stringifying body itself.
		// Otherwise .NET [FromBody] parser doesn't work
		if (req.url.match(new RegExp('^' + environment.origin)) && !req.headers.get('Content-Type') && (typeof req.body === 'string' || typeof req.body === 'number')) {
			req = req.clone({
				body: JSON.stringify(req.body),
				setHeaders: {
					'Content-Type': 'application/json',
				},
			});
		}

		// Setting config via HttpHeader and removing custom headers
		if (req.headers.get(config.errorsHeader)) {
			config.showErrors = req.headers.get(config.errorsHeader) !== 'false';
			req = req.clone({
				headers: req.headers.delete(config.errorsHeader),
			});
		}

		// Set the result of the analytics tracking opt in/out.
		if (req.url.match(environment.origin)) {
			req = req.clone({
				headers: req.headers.set('Has-Opted-In-Tracking', String(this.analyticsService.hasOptedInTracking)),
			});
		}

		return next.handle(req).pipe(
			catchError(error => {
				if (error instanceof HttpErrorResponse && config.showErrors) {
					// API is down - showing maintenance page
					if ((error.status === 0 || error.status === 503) && req.url.match(new RegExp('^' + environment.origin))) {
						this.router.navigate(['/under-maintenance'], {
							queryParams: {
								redirect: window.location.pathname,
							},
						});
					}
					// API's 404 is HTML. Not showing it in Toastr
					else if (typeof error.error === 'string' && !error.error.match(/^<!DOCTYPE/)) {
						this.toastrService.error(error.error);
					}
					// Processing modelState errors
					else if (error.error && typeof error.error['modelState'] === 'object' && Object.keys(error.error['modelState']).length) {
						const modelStates: string[][] = Object.keys(error.error['modelState']).map(key => error.error['modelState'][key]);
						modelStates.map(state =>
							state.map(err => {
								if (typeof err === 'string') this.toastrService.error(err);
							})
						);
					} else if (error.error && (error.error.error_description || error.error.message)) {
						this.toastrService.error(error.error.error_description || error.error.message);
					} else if (typeof error.message === 'string') {
						this.toastrService.error(error.message);
					} else {
						this.toastrService.error('Something went wrong. Please try again.');
					}
				}
				return throwError(error);
			})
		);
	}
}
